import * as Sentry from '@sentry/browser';
import { arrOfObjectstoKeyedJSONObj } from '../../../utils/dataProcessing';

/**
 * Retrieves the conversion rates for a specified template and coach from big-query through a firebase function.
 * It processes the response to return an object where each key corresponds to a template version
 * and its associated data object.
 *
 * @param {Function} remote - The function to call the remote service.
 * @param {string} coachId - The coach id whose template conversion rates are being requested.
 * @param {string} templateName - The name of the template for which conversion rates are being fetched.
 * @returns {Promise<Object>} Resolves to an object mapping template versions to it's conversion data.
 *                            If the request fails or no data is available, the promise resolves to an empty object.
 */
const getTemplateConversionRates = async (remote, coachId, templateName) => {
  const response = await remote('getTemplateConversionRates', {
    coachId,
    templateName,
  }, {
    method: 'POST',
  });

  if (!response.ok) {
    Sentry.captureException(
      new Error(`Error in retrieving conversion rates for template ${templateName} of coach ${coachId}`),
    );
    return {};
  }

  const { data: templateConversionRatesArr = [] } = await response.json();
  const templateConversionRates = arrOfObjectstoKeyedJSONObj(templateConversionRatesArr, 'templateVersion');

  return templateConversionRates;
};

/**
 * Media an RCS message can carry. RCS takes no QuickTime (.mov) or HEIC, so an RCS template's video
 * has to be an mp4: Twilio accepts anything else and then fails it as undeliverable (63021).
 * https://www.twilio.com/docs/messaging/guides/accepted-mime-types
 */
const RCS_ATTACHMENT_TYPES = ['video/mp4', 'image/jpeg', 'image/png', 'image/gif'];

/** RCS media size limit (Twilio: 16 MB). */
const RCS_MAX_ATTACHMENT_MB = 16;
const RCS_MAX_ATTACHMENT_BYTES = RCS_MAX_ATTACHMENT_MB * 1000 * 1000;

/**
 * Why a file cannot be the attachment of an RCS template.
 * @enum {string}
 */
const RcsAttachmentIssue = {
  TYPE: 'TYPE',
  SIZE: 'SIZE',
};

/**
 * Checks a file picked for an RCS template's attachment.
 *
 * @param {File} file - The picked file
 * @returns {string|null} The RcsAttachmentIssue, or null when RCS can send it
 */
const getRcsAttachmentIssue = (file) => {
  if (!RCS_ATTACHMENT_TYPES.includes(file.type)) {
    return RcsAttachmentIssue.TYPE;
  }
  if (file.size > RCS_MAX_ATTACHMENT_BYTES) {
    return RcsAttachmentIssue.SIZE;
  }
  return null;
};

export {
  getTemplateConversionRates,
  getRcsAttachmentIssue,
  RcsAttachmentIssue,
  RCS_ATTACHMENT_TYPES,
  RCS_MAX_ATTACHMENT_MB,
};
